import { Bundle } from 'src/models/bundle';
import { useCallback, useMemo } from 'react';
import { BleService } from 'src/ble/BleService';
import { BundleTranslator } from '../../utils/helpers/BundleTranslator';
import { useIntl } from 'react-intl';
import { BundleMessages } from 'src/intl/BundleMessages';
import { useSelector } from 'react-redux';
import { selectBleBundleFiles } from 'src/redux/ble/selectors';
import { BleControllerFile } from 'src/redux/ble/types';
import { useToast } from 'src/components/reusable-components/useToast';

interface UseBundleActionsResult {
  /** Information about the bundle file on the controller, if it exists. */
  installedBundleFile: BleControllerFile | undefined;
  /** Writes the provided bundle to the controller. */
  writeBundle: () => Promise<void>;
  /** Removes the bundle from the controller, if it exists. */
  deleteBundle: () => Promise<void>;
}

export const useBundleActions = (bundle?: Bundle): UseBundleActionsResult => {
  const { formatMessage } = useIntl();
  const [present] = useToast();

  const bundleFiles = useSelector(selectBleBundleFiles);

  const installedBundleFile = useMemo(
    () => (bundle?._id ? bundleFiles.find(i => i.bundleId === bundle._id) : undefined),
    [bundle?._id, bundleFiles],
  );

  const writeBundle = useCallback(async () => {
    try {
      if (!bundle) throw new Error('No bundle provided!');
      if (!BleService.controller) throw new Error('BleControllerService not accessible!');
      const tubes = BundleTranslator.transformTubes(bundle);

      await BleService.controller.writeBundle({
        tubes: tubes,
        bundleId: bundle._id,
        isRotated: bundle.rotated,
        bundlePattern: bundle.pattern,
        name: bundle.name,
        siteName: bundle.siteName,
      });
      present(
        {
          message: formatMessage(BundleMessages.installSuccess, { bundleName: bundle.name }),
        },
        'success',
      );
    } catch (err) {
      console.error(`Failed to install bundle:`, err);
      present(
        {
          message: formatMessage(BundleMessages.installFailure, { bundleName: bundle?.name ?? '?' }),
        },
        'danger',
      );
    }
  }, [bundle, formatMessage, present]);

  const deleteBundle = useCallback(async () => {
    try {
      if (!bundle?._id) throw new Error('No bundle provided!');
      if (!BleService.controller) throw new Error('BleControllerService not accessible!');
      await BleService.controller.deleteBundle(bundle._id);
      present(
        {
          message: formatMessage(BundleMessages.uninstallSuccess, { bundleName: bundle.name }),
        },
        'success',
      );
    } catch (err) {
      console.error(`Failed to uninstall bundle:`, err);
      present(
        {
          message: formatMessage(BundleMessages.uninstallFailure, { bundleName: bundle?.name ?? '?' }),
        },
        'danger',
      );
    }
  }, [bundle?._id, bundle?.name, formatMessage, present]);

  return { writeBundle, deleteBundle, installedBundleFile };
};
